import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Alert,
    Divider,
    Grid,
    Card,
    CardContent,
    CardHeader,
    TextField,
    MenuItem,
    useTheme,
    useMediaQuery,
    Container,
    Chip,
    Stack,
    Fade,
    Tooltip,
    Button,
    AppBar,
    Toolbar,
    IconButton,
    Collapse
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { SpinningGradelyLoader, FloatingGradelyLoader } from "../../loadingIcons/LoadingIcons";

import SchoolIcon from '@mui/icons-material/School';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PsychologyIcon from '@mui/icons-material/Psychology';
import WavingHandIcon from '@mui/icons-material/WavingHand';
import BookIcon from '@mui/icons-material/Book';
import InfoIcon from '@mui/icons-material/Info';
import LogoutIcon from '@mui/icons-material/Logout';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import StarIcon from '@mui/icons-material/Star';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import TargetIcon from '@mui/icons-material/MyLocation';
import BrainIcon from '@mui/icons-material/Psychology';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ReportCardIcon from '@mui/icons-material/Assessment';

const StudentDashboard = (props) => {
    const location = useLocation();
    const studentId = props.studentId || location.state?.studentId;
    const fullName = props.fullName || location.state?.fullName;
    const pin = props.pin || location.state?.pin;
    const theme = useTheme();

    // Enhanced responsive breakpoints
    const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600px - 900px
    const isDesktop = useMediaQuery(theme.breakpoints.up('md')); // >= 900px
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('xs')); // < 400px
    const isExtraSmall = useMediaQuery('(max-width:350px)'); // < 350px

    const [loading, setLoading] = useState(true);
    const [allCards, setAllCards] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [error, setError] = useState("");
    const [studentName, setStudentName] = useState("");
    const [subjectAssessmentMap, setSubjectAssessmentMap] = useState({});
    const [selectedTerm, setSelectedTerm] = useState("");
    const [showDOKSection, setShowDOKSection] = useState(false);
    const [showProficiencySection, setShowProficiencySection] = useState(false);
    const navigate = useNavigate();

    const [subjectPlaceholders, setSubjectPlaceholders] = useState({});
    const getSubjectPlaceholder = (subjectName) => {
        return subjectPlaceholders[subjectName] || {
            icon: iconMap.default,
            description: 'Explore various topics and develop essential learning skills.',
            topics: ['Core Topics', 'Key Concepts', 'Practical Skills'],
            progress: 'N/A',
            highestDOKs: []
        };
    };

    const iconMap = {
        math: <TrendingUpIcon sx={{ fontSize: 24, color: '#2196f3' }} />,
        science: <PsychologyIcon sx={{ fontSize: 24, color: '#4caf50' }} />,
        english: <AssignmentIcon sx={{ fontSize: 24, color: '#ff9800' }} />,
        history: <SchoolIcon sx={{ fontSize: 24, color: '#9c27b0' }} />,
        default: <SchoolIcon sx={{ fontSize: 24, color: '#607d8b' }} />
    };

    useEffect(() => {
        const placeholderMap = {};

        allCards.forEach((card) => {
            const subject = card.subject;
            const entries = card.entries || [];
            const unit = card.unit?.trim();

            if (!placeholderMap[subject]) {
                const achieved = entries.filter(e => e.is_achieved).length;
                const total = entries.length;
                const progress = total > 0 ? Math.round((achieved / total) * 100) : 0;

                const topics = entries
                    .map(e => e.skill.split(/[.?!]/)[0].trim())
                    .filter((s, i, arr) => s && arr.indexOf(s) === i)
                    .slice(0, 3);

                const key = subject.toLowerCase();
                const iconKey = Object.keys(iconMap).find(k => key.includes(k)) || 'default';

                placeholderMap[subject] = {
                    icon: iconMap[iconKey],
                    description: `This subject focuses on ${topics.join(', ')}`.substring(0, 120),
                    topics,
                    progress: `${progress}%`,
                    highestDOKs: []
                };
            }

            // Only if unit is defined and non-empty
            if (unit) {
                const dok = Math.max(...entries.map(e => parseInt(e.dok_level || 1)));
                placeholderMap[subject].highestDOKs.push({ unit, dok });
            }
        });

        setSubjectPlaceholders(placeholderMap);
    }, [allCards]);

    useEffect(() => {
        const fetchSubjects = async () => {
            setError("");
            setSubjects([]);
            setStudentName("");

            try {
                const res = await axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/grades/public-dashboard/`,
                    { student_id: studentId, full_name: fullName, pin }
                );
                const cards = res.data.progress_cards || [];
                const assessmentMap = res.data.subject_assessment || {};
                setAllCards(cards);
                setSubjectAssessmentMap(assessmentMap);
                setStudentName(res.data.student);
                localStorage.setItem("student_dashboard_data", JSON.stringify(res.data));

            } catch (err) {
                console.error("❌ Error:", err);
                const msg = err.response?.data?.error || "Something went wrong.";
                setError(msg);
            } finally {
                setLoading(false);
            }
        };

        fetchSubjects();
    }, [studentId, fullName, pin]);

    useEffect(() => {
        if (!selectedTerm) {
            setSubjects([]);
            return;
        }

        const seen = new Set();
        const filteredSubjects = [];

        allCards
            .filter(card => card.term_number === parseInt(selectedTerm))
            .forEach(card => {
                if (!seen.has(card.subject)) {
                    seen.add(card.subject);
                    filteredSubjects.push({ subject: card.subject, subject_id: card.subject_id });
                }
            });

        setSubjects(filteredSubjects);
    }, [selectedTerm, allCards]);

    const handleCardClick = (subject, subject_id, selectedTerm) => {
        navigate(`/student/subject/${subject_id}`, {
            state: {
                studentId,
                fullName,
                pin,
                subjectName: subject,
                subject_id: subject_id,
                selectedTerm: selectedTerm
            },
        });
    };

    const getSubjectAssessment = (subjectName) => {
        const index = Object.keys(subjectAssessmentMap).find((key) => {
            const comment = subjectAssessmentMap[key];
            return comment && comment.summary?.includes(subjectName);
        });
        return index ? subjectAssessmentMap[index]?.overall_assessment || "Not Available" : "Not Available";
    };

    const getDOKColor = (level) => {
        if (level >= 4) return '#388e3c'; // green
        if (level === 3) return '#7cb342'; // light green
        if (level === 2) return '#fb8c00'; // orange
        return '#e53935'; // red
    };

    const handleLogout = () => {
        // Clear any stored data
        localStorage.removeItem("student_dashboard_data");
        // Navigate to student access page
        navigate('/student-access');
    };

    // DOK Levels data for educational section
    const dokLevels = [
        {
            level: 1,
            title: "Recall & Reproduction",
            description: "Basic recall of facts, terms, and simple procedures",
            icon: <BrainIcon sx={{ fontSize: 32, color: '#e53935' }} />,
            examples: ["List", "Define", "Identify", "Recall"],
            color: '#e53935'
        },
        {
            level: 2,
            title: "Skills & Concepts",
            description: "Use of information and conceptual knowledge",
            icon: <TargetIcon sx={{ fontSize: 32, color: '#fb8c00' }} />,
            examples: ["Compare", "Classify", "Organize", "Summarize"],
            color: '#fb8c00'
        },
        {
            level: 3,
            title: "Strategic Thinking",
            description: "Reasoning, planning, and using evidence",
            icon: <RocketLaunchIcon sx={{ fontSize: 32, color: '#7cb342' }} />,
            examples: ["Analyze", "Evaluate", "Create", "Design"],
            color: '#7cb342'
        },
        {
            level: 4,
            title: "Extended Thinking",
            description: "Complex reasoning, planning, and investigation",
            icon: <StarIcon sx={{ fontSize: 32, color: '#388e3c' }} />,
            examples: ["Synthesize", "Design", "Develop", "Create"],
            color: '#388e3c'
        }
    ];

    // Proficiency Scale data
    const proficiencyLevels = [
        {
            level: "Emerging",
            icon: <RadioButtonUncheckedIcon sx={{ fontSize: 32, color: '#f44336' }} />,
            description: "Beginning to develop understanding and skills",
            color: '#f44336'
        },
        {
            level: "Developing",
            icon: <HelpOutlineIcon sx={{ fontSize: 32, color: '#ff9800' }} />,
            description: "Shows some understanding and application",
            color: '#ff9800'
        },
        {
            level: "Proficient",
            icon: <CheckCircleIcon sx={{ fontSize: 32, color: '#4caf50' }} />,
            description: "Demonstrates expected understanding and skills",
            color: '#4caf50'
        },
        {
            level: "Extending",
            icon: <TrendingUpIcon sx={{ fontSize: 32, color: '#2196f3' }} />,
            description: "Exceeds expectations with deep understanding",
            color: '#2196f3'
        }
    ];

    return (
        <Box sx={{ backgroundColor: '#f5f5dc', minHeight: '100vh' }}>
            {/* Header with Logout */}
            <AppBar position="static" elevation={0} sx={{
                backgroundColor: 'white',
                borderBottom: '1px solid #e0e0e0',
                color: 'text.primary'
            }}>
                <Toolbar sx={{ justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <ReportCardIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                        <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                            Student Report Card
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        startIcon={<LogoutIcon />}
                        onClick={handleLogout}
                        sx={{
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            '&:hover': {
                                backgroundColor: 'primary.main',
                                color: 'white'
                            }
                        }}
                    >
                        Logout
                    </Button>
                </Toolbar>
            </AppBar>

            <Container maxWidth="lg" sx={{
                py: { xs: 3, sm: 4, md: 6 },
                px: { xs: 2, sm: 3, md: 4 }
            }}>
                {/* Welcome Hero Section */}
                <Fade in timeout={800}>
                    <Paper elevation={0} sx={{
                        background: 'linear-gradient(135deg, #6b8e23 0%, #8fbc8f 100%)',
                        borderRadius: 4,
                        color: 'white',
                        mb: 4,
                        overflow: 'hidden',
                        position: 'relative'
                    }}>
                        <Box sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            width: '50%',
                            height: '100%',
                            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Cpath d="M30 30c0-11.046-8.954-20-20-20s-20 8.954-20 20 8.954 20 20 20 20-8.954 20-20zm10 0c0-11.046-8.954-20-20-20s-20 8.954-20 20 8.954 20 20 20 20-8.954 20-20z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
                            opacity: 0.1
                        }} />
                        <Box sx={{ p: { xs: 4, sm: 6, md: 8 }, position: 'relative' }}>
                            <Grid container spacing={4} alignItems="center">
                                <Grid item xs={12} md={8}>
                                    <Typography
                                        variant={isMobile ? "h4" : "h3"}
                                        fontWeight="800"
                                        sx={{
                                            mb: 2,
                                            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                                            textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                        }}
                                    >
                                        🎓 Welcome Back, {studentName}!
                                    </Typography>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            mb: 3,
                                            opacity: 0.9,
                                            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' }
                                        }}
                                    >
                                        Your personalized learning journey awaits. Track your progress, explore achievements, and see how far you've come!
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Box sx={{
                                        textAlign: 'center',
                                        '& svg': {
                                            fontSize: { xs: 80, sm: 100, md: 120 },
                                            opacity: 0.8,
                                            filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))'
                                        }
                                    }}>
                                        <SchoolIcon sx={{ color: 'white' }} />
                                    </Box>
                                </Grid>
                            </Grid>

                        </Box>
                    </Paper>
                </Fade>

                {/* Term Selection Card */}
                <Fade in timeout={1000}>
                    <Paper elevation={2} sx={{
                        p: 4,
                        borderRadius: 3,
                        mb: 4,
                        backgroundColor: 'white'
                    }}>
                        <Typography variant="h5" fontWeight="bold" sx={{ mb: 3, color: 'primary.main' }}>
                            📚 Select Your Term
                        </Typography>
                        <TextField
                            select
                            fullWidth
                            label="Choose your current term to view progress"
                            value={selectedTerm}
                            onChange={(e) => setSelectedTerm(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    fontSize: { xs: '1rem', sm: '1.1rem' }
                                }
                            }}
                        >
                            {[1, 2, 3].map(term => (
                                <MenuItem key={term} value={term}>
                                    📅 Term {term}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Paper>
                </Fade>

                {/* Loading State */}
                {loading && (
                    <Box display="flex" justifyContent="center" my={4}>
                        <CircularProgress size={isMobile ? 40 : 60} />
                    </Box>
                )}

                {/* Error State */}
                {error && (
                    <Alert severity="error" sx={{ mt: 3, borderRadius: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Subject Cards */}
                {!loading && !error && selectedTerm && subjects.length > 0 && (
                    <Fade in timeout={1200}>
                        <Paper elevation={2} sx={{
                            p: 4,
                            borderRadius: 3,
                            mb: 4,
                            backgroundColor: '#fffef7',
                            border: '1px solid #d4c5a9'
                        }}>
                            <Typography
                                variant="h5"
                                fontWeight="bold"
                                sx={{
                                    mb: 3,
                                    color: 'primary.main',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}
                            >
                                <BookIcon sx={{ fontSize: '2rem' }} />
                                📊 Your Subjects - Term {selectedTerm}
                            </Typography>

                            <Grid container spacing={3}>
                                {subjects.map((subj, index) => {
                                    const assessment = getSubjectAssessment(subj.subject);
                                    const placeholder = getSubjectPlaceholder(subj.subject);

                                    return (
                                        <Grid item xs={12} sm={6} lg={4} key={subj.subject || `subject-${index}`}>
                                            <Card
                                                elevation={0}
                                                onClick={() => handleCardClick(subj.subject, subj.subject_id, selectedTerm)}
                                                sx={{
                                                    background: 'linear-gradient(135deg, #6b8e23 0%, #8fbc8f 100%)',
                                                    borderRadius: 3,
                                                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                                    cursor: "pointer",
                                                    height: "100%",
                                                    minHeight: 280,
                                                    position: 'relative',
                                                    overflow: 'hidden',
                                                    color: 'white',
                                                    border: '2px solid transparent',
                                                    '&:hover': {
                                                        transform: "translateY(-8px) scale(1.02)",
                                                        boxShadow: "0 20px 40px rgba(107, 142, 35, 0.25)",
                                                        border: '2px solid rgba(255,255,255,0.3)'
                                                    },
                                                    '&:active': {
                                                        transform: "translateY(-4px) scale(1.01)",
                                                    }
                                                }}
                                            >
                                                {/* Background Pattern */}
                                                <Box sx={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    right: 0,
                                                    width: '100%',
                                                    height: '100%',
                                                    background: 'url("data:image/svg+xml,%3Csvg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Cpath d="M20 20c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10zm10 0c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10z"/%3E%3C/g%3E%3C/svg%3E")',
                                                    opacity: 0.6
                                                }} />
                                                <CardHeader
                                                    avatar={
                                                        <Box sx={{
                                                            p: 1.5,
                                                            bgcolor: 'rgba(255,255,255,0.2)',
                                                            borderRadius: '50%',
                                                            backdropFilter: 'blur(10px)'
                                                        }}>
                                                            {React.cloneElement(placeholder.icon, {
                                                                sx: { fontSize: 28, color: 'white' }
                                                            })}
                                                        </Box>
                                                    }
                                                    title={
                                                        <Typography
                                                            variant="h5"
                                                            fontWeight="bold"
                                                            sx={{
                                                                fontSize: { xs: '1.2rem', sm: '1.3rem' },
                                                                color: 'white',
                                                                textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                            }}
                                                        >
                                                            {subj.subject}
                                                        </Typography>
                                                    }
                                                    subheader={
                                                        <Chip
                                                            label={assessment}
                                                            size="small"
                                                            sx={{
                                                                mt: 1,
                                                                bgcolor: 'rgba(255,255,255,0.2)',
                                                                color: 'white',
                                                                fontWeight: 'bold',
                                                                fontSize: '0.8rem',
                                                                backdropFilter: 'blur(10px)',
                                                                border: '1px solid rgba(255,255,255,0.3)'
                                                            }}
                                                        />
                                                    }
                                                    sx={{
                                                        pb: 1,
                                                        position: 'relative',
                                                        zIndex: 1,
                                                        '& .MuiCardHeader-content': {
                                                            minWidth: 0
                                                        }
                                                    }}
                                                />
                                                <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight="bold"
                                                        sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}
                                                    >
                                                        🎯 Key Topics:
                                                    </Typography>
                                                    <Stack direction="row" flexWrap="wrap" gap={0.5} mb={2}>
                                                        {placeholder.topics.slice(0, 3).map((topic, idx) => (
                                                            <Chip
                                                                key={idx}
                                                                label={topic}
                                                                size="small"
                                                                sx={{
                                                                    fontSize: '0.75rem',
                                                                    bgcolor: 'rgba(255,255,255,0.2)',
                                                                    color: 'white',
                                                                    backdropFilter: 'blur(10px)',
                                                                    border: '1px solid rgba(255,255,255,0.3)'
                                                                }}
                                                            />
                                                        ))}
                                                    </Stack>

                                                    <Typography
                                                        variant="body2"
                                                        fontWeight="bold"
                                                        sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}
                                                    >
                                                        🏆 Achievements:
                                                    </Typography>
                                                    <Stack spacing={0.5}>
                                                        {placeholder.highestDOKs.slice(0, 2).map(({ unit, dok }, idx) => (
                                                            <Chip
                                                                key={idx}
                                                                label={`${unit.length > 15 ? unit.substring(0, 15) + '...' : unit}: DOK ${dok}`}
                                                                size="small"
                                                                sx={{
                                                                    fontSize: '0.75rem',
                                                                    fontWeight: '600',
                                                                    bgcolor: 'rgba(255,255,255,0.25)',
                                                                    color: 'white',
                                                                    backdropFilter: 'blur(10px)',
                                                                    border: '1px solid rgba(255,255,255,0.4)'
                                                                }}
                                                            />
                                                        ))}
                                                        {placeholder.highestDOKs.length === 0 && (
                                                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontStyle: 'italic' }}>
                                                                Click to explore your progress!
                                                            </Typography>
                                                        )}
                                                    </Stack>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Paper>
                    </Fade>
                )}

                {/* No Subjects Selected */}
                {!loading && !error && selectedTerm && subjects.length === 0 && (
                    <Fade in timeout={1200}>
                        <Paper elevation={2} sx={{
                            p: 6,
                            borderRadius: 3,
                            textAlign: 'center',
                            backgroundColor: '#fffef7',
                            border: '1px solid #d4c5a9',
                            mb: 4
                        }}>
                            <SchoolIcon sx={{ fontSize: 80, mb: 2, color: 'primary.main', opacity: 0.7 }} />
                            <Typography variant="h5" sx={{ mb: 1, color: 'primary.main' }}>
                                No subjects available for Term {selectedTerm}
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                Please check back later or contact your teacher.
                            </Typography>
                        </Paper>
                    </Fade>
                )}

                {/* Educational Sections for Parents */}
                {!loading && (
                    <>
                        {/* DOK Levels Section */}
                        <Fade in timeout={1400}>
                            <Paper elevation={2} sx={{
                                p: 4,
                                borderRadius: 3,
                                mb: 4,
                                backgroundColor: '#fffef7',
                            border: '1px solid #d4c5a9'
                            }}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 3,
                                    cursor: 'pointer'
                                }}
                                    onClick={() => setShowDOKSection(!showDOKSection)}
                                >
                                    <Typography variant="h5" fontWeight="bold" sx={{ color: 'primary.main' }}>
                                        🧠 Understanding DOK Levels
                                    </Typography>
                                    <IconButton>
                                        {showDOKSection ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                </Box>
                                <Collapse in={showDOKSection}>
                                    <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                                        Depth of Knowledge (DOK) levels help measure how deeply students think about and understand concepts.
                                    </Typography>
                                    <Grid container spacing={3}>
                                        {dokLevels.map((level) => (
                                            <Grid item xs={12} sm={6} md={3} key={level.level}>
                                                <Card elevation={0} sx={{
                                                    p: 3,
                                                    height: '100%',
                                                    border: '2px solid',
                                                    borderColor: level.color + '40',
                                                    borderRadius: 2,
                                                    backgroundColor: '#f9f7f4'
                                                }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                        <Box sx={{
                                                            p: 1,
                                                            bgcolor: level.color + '20',
                                                            borderRadius: '50%',
                                                            mr: 2
                                                        }}>
                                                            {level.icon}
                                                        </Box>
                                                        <Typography variant="h6" fontWeight="bold" sx={{ color: level.color }}>
                                                            Level {level.level}
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                                                        {level.title}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                                                        {level.description}
                                                    </Typography>
                                                    <Box>
                                                        <Typography variant="caption" fontWeight="bold" sx={{ color: level.color }}>
                                                            Examples:
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                                            {level.examples.map((example, idx) => (
                                                                <Chip
                                                                    key={idx}
                                                                    label={example}
                                                                    size="small"
                                                                    sx={{
                                                                        bgcolor: level.color + '30',
                                                                        color: level.color,
                                                                        fontSize: '0.7rem'
                                                                    }}
                                                                />
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                </Card>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Collapse>
                            </Paper>
                        </Fade>

                        {/* Proficiency Scale Section */}
                        <Fade in timeout={1600}>
                            <Paper elevation={2} sx={{
                                p: 4,
                                borderRadius: 3,
                                mb: 4,
                                backgroundColor: '#fffef7',
                            border: '1px solid #d4c5a9'
                            }}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 3,
                                    cursor: 'pointer'
                                }}
                                    onClick={() => setShowProficiencySection(!showProficiencySection)}
                                >
                                    <Typography variant="h5" fontWeight="bold" sx={{ color: 'primary.main' }}>
                                        🏆 Understanding Proficiency Scale
                                    </Typography>
                                    <IconButton>
                                        {showProficiencySection ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                </Box>
                                <Collapse in={showProficiencySection}>
                                    <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                                        The proficiency scale shows how well students meet learning goals, focusing on progress rather than grades.
                                    </Typography>
                                    <Grid container spacing={3} sx={{ mb: 4 }}>
                                        {proficiencyLevels.map((level) => (
                                            <Grid item xs={12} sm={6} md={3} key={level.level}>
                                                <Card elevation={0} sx={{
                                                    p: 3,
                                                    height: '100%',
                                                    border: '2px solid',
                                                    borderColor: level.color + '40',
                                                    borderRadius: 2,
                                                    backgroundColor: '#f9f7f4',
                                                    textAlign: 'center'
                                                }}>
                                                    <Box sx={{
                                                        p: 1.5,
                                                        bgcolor: level.color + '20',
                                                        borderRadius: '50%',
                                                        display: 'inline-block',
                                                        mb: 2
                                                    }}>
                                                        {level.icon}
                                                    </Box>
                                                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, color: level.color }}>
                                                        {level.level}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                        {level.description}
                                                    </Typography>
                                                </Card>
                                            </Grid>
                                        ))}
                                    </Grid>
                                    <Paper elevation={0} sx={{
                                        background: 'linear-gradient(135deg, #6b8e23 0%, #8fbc8f 100%)',
                                        p: 4,
                                        borderRadius: 2,
                                        color: 'white',
                                        textAlign: 'center'
                                    }}>
                                        <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
                                            🌟 Moving Beyond Traditional Grades
                                        </Typography>
                                        <Grid container spacing={4}>
                                            <Grid item xs={12} md={4}>
                                                <Typography variant="h3" fontWeight="bold" sx={{ mb: 1 }}>4</Typography>
                                                <Typography variant="body1">Proficiency Levels</Typography>
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <Typography variant="h3" fontWeight="bold" sx={{ mb: 1 }}>100%</Typography>
                                                <Typography variant="body1">Clear Communication</Typography>
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <Typography variant="h3" fontWeight="bold" sx={{ mb: 1 }}>∞</Typography>
                                                <Typography variant="body1">Growth Potential</Typography>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </Collapse>
                            </Paper>
                        </Fade>
                    </>
                )}

                {/* Loading State */}
                {loading && (
                    <Box
                        sx={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            width: "100vw",
                            height: "100vh",
                            backgroundColor: "rgba(255,255,255,0.9)",
                            backdropFilter: "blur(8px)",
                            zIndex: 2000,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Paper
                            elevation={8}
                            sx={{
                                p: { xs: 3, sm: 4 },
                                borderRadius: 4,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                backgroundColor: "white",
                                maxWidth: 400,
                                mx: 2
                            }}
                        >
                            <FloatingGradelyLoader
                                size={60}
                                showText={true}
                                text="Loading student dashboard and progress data..."
                            />
                        </Paper>
                    </Box>
                )}
            </Container>
        </Box>
    );
};

export default StudentDashboard;
